import type { ErrorEvent, EventHint } from '@sentry/nextjs'

/**
 * Pure event-drop logic for Sentry's `beforeSend`, extracted from
 * instrumentation-client so it can be unit-tested and reasoned about in one
 * place. `beforeSend` runs on the RAW (un-symbolicated) event in the browser:
 * stack frame paths read as minified chunk URLs, so frame-based matching is
 * unreliable — most rules match on the (never source-mapped) message string.
 */

type StackFrame = {
  filename?: string
  abs_path?: string
  module?: string
  function?: string
}

export const isTheoPlayerFrame = (f: StackFrame): boolean =>
  /theo\s*player|\/theo/i.test(
    `${f.filename ?? ''} ${f.abs_path ?? ''} ${f.module ?? ''} ${f.function ?? ''}`,
  )

export const isBlazeFrame = (f: StackFrame): boolean =>
  /blaze-web-sdk|wscsports/i.test(`${f.filename ?? ''} ${f.abs_path ?? ''} ${f.module ?? ''}`)

/**
 * A ChunkLoadError means the browser tried to fetch a JS chunk that no longer
 * exists — almost always a client holding stale chunk hashes after a new deploy.
 * Detected by error name or message; used by the auto-reload handler.
 */
export const isChunkLoadError = (value: unknown): boolean => {
  if (!value) return false
  const name = (value as { name?: string }).name
  if (name === 'ChunkLoadError') return true
  const message = typeof value === 'string' ? value : (value as { message?: string }).message
  return typeof message === 'string' &&
    /loading chunk [\w-]+ failed|failed to load chunk|chunkloaderror/i.test(message)
}

const isManualCapture = (event: ErrorEvent): boolean =>
  event.tags?.manual_capture === 'true' || event.extra?.manual_capture === true

// Expected API errors (IError shape) surfaced as unhandled promise rejections,
// already handled by React Query onError. Plain objects with no stack → they
// show up as unactionable <unknown> events.
const isHandledApiRejection = (originalException: EventHint['originalException']): boolean =>
  originalException !== null &&
  typeof originalException === 'object' &&
  !(originalException instanceof Error) &&
  'code' in originalException &&
  'userMessage' in originalException

// A promise rejected with a DOM Event (type=error) rather than an Error —
// typically a failed resource/chunk load. Carries no message/stack, so Sentry
// logs it as <unknown>. See issue 7219543691.
const isDomEventRejection = (originalException: EventHint['originalException']): boolean =>
  typeof Event !== 'undefined' && originalException instanceof Event

// iOS Safari Firebase-Auth IndexedDB persistence noise. See FZ-WEB-NEXTJS-3N.
const isIndexedDbError = (event: ErrorEvent): boolean =>
  event.exception?.values?.some(
    (ex) =>
      ex.type === 'UnknownError' &&
      typeof ex.value === 'string' &&
      /indexed\s*db|idb/i.test(ex.value),
  ) ?? false

// THEOplayer destroy-race null access. The minified prop name (`kU`) changes per
// release and browsers phrase the null access differently, so match the error
// shape + a theoplayer frame when available, and the `kU` token regardless of
// frames (frames are minified at beforeSend). See FZ-WEB-NEXTJS-KV/MK/FR and
// issue 7490567334.
const isTheoPlayerNullRace = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    if (ex.type !== 'TypeError' || typeof ex.value !== 'string') return false

    const message = ex.value
    const hasTheoFrame = (ex.stacktrace?.frames ?? []).some(isTheoPlayerFrame)

    if (hasTheoFrame && /null|undefined/i.test(message)) return true
    if (hasTheoFrame && /reading '(?:kU|getItem)'/i.test(message)) return true

    // Chrome:  "Cannot read properties of null (reading 'kU')"
    // Safari:  "null is not an object (evaluating 'e.kU')"
    // Firefox: "can't access property \"kU\", e is null" (double quotes,
    //          no reading/evaluating — see issue 7599342919)
    if (/(?:reading|evaluating) '[^']*\bkU'/i.test(message)) return true
    if (/access property "[^"]*\bkU"/i.test(message)) return true

    return false
  }) ?? false

// THEOplayer internal UI menu component: a `window` `resize` listener
// registered by the menu (e.g. settings/quality menu) fires after the
// underlying element has already been torn down — `this.el()` returns
// `undefined`/non-Element, so the SDK's own `getComputedStyle(this.el())`
// call throws inside THEOplayer's minified UI code. Every frame in these
// events is inside `theoplayer`'s bundle or the Sentry auto-instrumentation
// wrapper — no app frame ever appears; this is a THEOplayer SDK bug, not
// something app code triggers or can catch (the listener lives entirely
// inside the third-party library). See FZ-WEB-NEXTJS-FC.
const isTheoPlayerMenuResizeRace = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    if (ex.type !== 'TypeError' || typeof ex.value !== 'string') return false
    if (!/getComputedStyle.*is not of type 'Element'/i.test(ex.value)) return false

    const frames = ex.stacktrace?.frames ?? []
    return frames.some(isTheoPlayerFrame)
  }) ?? false

// THEOplayer's own play/pause button handler (`handleClick` in the minified
// bundle) calls `this.t.paused() ? this.t.play() : this.t.pause()` with no
// guard against the player's paused state changing between that check and the
// call itself (e.g. rapid clicks, autoplay racing a user click, or unmount
// tearing down the player mid-toggle). `play()`'s returned promise then
// rejects with a bare `AbortError: Aborted` DOMException when the request is
// interrupted — normal browser media behavior, not our bug. Every frame lives
// entirely inside THEOplayer's own bundle, reached via Sentry's global
// `onunhandledrejection` handler; our integration (src/components/Player/**)
// never calls play()/pause() directly. Same class of issue as the already-
// filtered fullscreen race (FZ-WEB-NEXTJS-FP). See FZ-WEB-NEXTJS-EZ, FZ-WEB-NEXTJS-EW.
const isTheoPlayerAbortRace = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    if (ex.type !== 'AbortError' && ex.type !== 'DOMException') return false
    if (typeof ex.value !== 'string' || !/^aborted$/i.test(ex.value.trim())) return false
    return (ex.stacktrace?.frames ?? []).some(isTheoPlayerFrame)
  }) ?? false

// Browser extensions and built-in wallets (Brave Wallet, MetaMask, …) inject
// inline scripts into the document. Those run as page-origin "global code":
// their stack frames' filenames are the page URL itself, never a .js file, so
// allowUrls and Sentry's extension heuristics both pass them through. Our code
// only ever executes from built .js bundles — an unhandled error whose every
// frame is a non-.js document script cannot be ours. Structural rule instead of
// per-plugin message patterns. See issue 7387086621 (window.ethereum, Brave iOS).
const isInjectedInlineScriptError = (event: ErrorEvent): boolean => {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type ?? ''
  // Only global handlers can catch injected-script errors; anything caught by
  // our own try/catch or React boundaries went through app code — keep those.
  if (!/onerror|global/i.test(mechanismType)) return false

  return event.exception?.values?.every((ex) => {
    const frames = ex.stacktrace?.frames ?? []
    // No frames at all → can't prove it's foreign; fail toward visibility.
    if (frames.length === 0) return false
    return frames.every((f) => {
      const file = f.filename ?? f.abs_path ?? ''
      return file !== '' && !/\.[cm]?js([?#]|$)/i.test(file)
    })
  }) ?? false
}

// THEOplayer's own fullscreen-button click handler calls
// `exitFullscreen()`/`requestFullscreen()` unconditionally based on
// `isFullscreen()`, with no guard for the state changing between that check
// and the call (e.g. the browser/OS already exited fullscreen — tab switch,
// Escape key, another fullscreen request elsewhere — in the gap between the
// click and the SDK's own dispatch). The document is no longer "active" for
// fullscreen purposes by the time the SDK calls exitFullscreen/requestFullscreen,
// so the browser throws. Every frame lives inside THEOplayer's own minified
// bundle (`i1.exitFullscreen`, `r.handleClick`) — no app-code frame — so this
// is the same class of third-party SDK timing bug as the `kU` null-race and
// getComputedStyle menu-resize rules below, not an app bug. See FZ-WEB-NEXTJS-FP.
const isTheoPlayerFullscreenRace = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    if (ex.type !== 'TypeError' || typeof ex.value !== 'string') return false
    if (!/(?:exit|request)Fullscreen/i.test(ex.value)) return false

    const frames = ex.stacktrace?.frames ?? []
    return frames.some(isTheoPlayerFrame)
  }) ?? false

// Blaze SDK init failures and internal TypeErrors. App-level WSC guards prevent
// most crashes; the rest originates inside blaze-web-sdk. See FZ-WEB-NEXTJS-EY,
// N6, N7, N8, and issue 7369412389 (icons).
const isBlazeNoise = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    const message = typeof ex.value === 'string' ? ex.value : ''

    // Message-only matches (Blaze-specific, safe without a frame):
    if (ex.type === 'TypeError' && /destructure property '(?:layout|hasStores)'/i.test(message)) {
      return true
    }
    // createNavigationButton reads this.playerLayout.icons.navigation; no app
    // code reads `.icons`. Chrome/Edge: "reading 'icons'"; Safari: "evaluating
    // 'e.playerLayout.icons'".
    if (ex.type === 'TypeError' && /reading 'icons'|evaluating '[^']*\.icons/i.test(message)) {
      return true
    }

    // Frame-gated matches (need a blaze frame to be safe — generic messages):
    const frames = ex.stacktrace?.frames ?? []
    if (!frames.some(isBlazeFrame)) return false

    if (ex.type === 'Error' && ex.value === 'Error in getAppConfigurations') return true

    if (ex.type !== 'TypeError' || !message) return false
    return /destructure property '(?:layout|hasStores)'|reading '(?:id|startsWith)'/i.test(message)
  }) ?? false

// A `window` `message` listener (registered by some vendor script — Recurly.js
// hosted-fields/3DS iframe bridge, an ad/analytics tag, etc.) assumes
// `event.data` is always a string and calls `.data.split(...)` on it directly.
// Any other script (browser extension, another vendor tag) that posts a
// non-string `data` to the SAME window (a number, object, or null) makes that
// unrelated listener throw. Sentry's own `addEventListener` auto-instrumentation
// (mechanism `auto.browser.browserapierrors.addEventListener`) is what reports
// it, not app code — every frame in these events is either that Sentry wrapper
// or anonymous/minified vendor code; no app frame ever appears (confirmed: no
// `addEventListener('message', …)` anywhere in src/). We can't fix a listener
// we don't own and didn't register. Message-only match because the minified
// variable name before `.data.split` is not stable across bundles/releases.
// See FZ-WEB-NEXTJS-F2.
const isForeignMessageDataSplitError = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((ex) => {
    if (ex.type !== 'TypeError' || typeof ex.value !== 'string') return false
    if (!/\.data\.split is not a function/i.test(ex.value)) return false

    const mechanismType = ex.mechanism?.type ?? event.exception?.values?.[0]?.mechanism?.type ?? ''
    return /browserapierrors\.addEventListener/i.test(mechanismType)
  }) ?? false

/**
 * Decide whether Sentry should drop an event in `beforeSend`. Returns `true` to
 * drop. Manual captures always survive; otherwise known noise is dropped and
 * only events carrying an exception are kept (this also drops captureMessage()
 * events — none exist today; allow `event.message` here if that changes).
 */
export function shouldDropEvent(event: ErrorEvent, hint?: EventHint): boolean {
  if (isManualCapture(event)) return false

  // Sentry's browser SDK reports this mechanism as
  // `auto.browser.global_handlers.onunhandledrejection`, not the bare
  // `onunhandledrejection` string — an exact-match check here never matched
  // in production, so isHandledApiRejection/isDomEventRejection never ran and
  // <unknown> DOM Event rejections kept shipping. See FZ-WEB-NEXTJS-2R.
  const mechanism = event.exception?.values?.[0]?.mechanism
  if (mechanism?.type?.endsWith('onunhandledrejection')) {
    const originalException = hint?.originalException
    if (isHandledApiRejection(originalException)) return true
    if (isDomEventRejection(originalException)) return true
  }

  if (isIndexedDbError(event)) return true
  if (isTheoPlayerNullRace(event)) return true
  if (isTheoPlayerMenuResizeRace(event)) return true
  if (isTheoPlayerFullscreenRace(event)) return true
  if (isTheoPlayerAbortRace(event)) return true
  if (isBlazeNoise(event)) return true
  if (isInjectedInlineScriptError(event)) return true
  if (isForeignMessageDataSplitError(event)) return true

  // Keep only events with an exception.
  return !event.exception
}
